'use client';
import React, { useEffect, useRef } from 'react';

import { IcChevronRight, theme, Typography } from '@bgzt/ui';

import lottie from 'lottie-web';
import { goToFullWebviewOnApp } from '~/services/app-interface';
import { useEnvironmentInfo } from '~/hooks';
import { TextModel } from '~/types/common';
import { Section } from '~/components';
import { TextModelRenderer } from '~/components/TextModelRenderer';

import styles from './SamsungCardBenefit.module.css';

type Props = {
  title: string;
  imageUrl: string;
  appUrl: string;
  webUrl: string;
  contents: TextModel[];
};

export const SamsungCardBenefit = ({ title, imageUrl, appUrl, webUrl, contents }: Props) => {
  const lottieRef = useRef<HTMLDivElement | null>(null);
  const { isWebview } = useEnvironmentInfo();

  const isLottieFile = (url: string) => {
    const regex = /(\.){1}json$/g;
    return regex.test(url);
  };
  const isLottie = isLottieFile(imageUrl);

  const handleClick = () => {
    isWebview ? goToFullWebviewOnApp({ url: appUrl }).go() : window.open(webUrl, '_blank', 'noopener noreferrer');
  };

  useEffect(() => {
    if (!imageUrl || !isLottie) return;

    const animation = lottie.loadAnimation({
      container: lottieRef.current as HTMLDivElement,
      loop: true,
      autoplay: true,
      path: imageUrl,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });

    return () => {
      animation?.destroy();
    };
  }, [imageUrl, isLottie]);

  return (
    <Section>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div ref={lottieRef} className={styles.lottie} />
          <div className={styles.contentWrapper}>
            <div className={styles.titleWrapper} onClick={handleClick}>
              <Typography variant="S4" as="p" color="gray-900">
                {title}
              </Typography>
              <IcChevronRight size={12} fill={theme.colors['gray-700']} />
            </div>
            <div className={styles.contents}>
              {contents.map((content) => (
                <TextModelRenderer key={content.main.text} textModel={content} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
